import { DateTime } from 'luxon';
const CALENDAR_WEEKS = 6;
const DAYS_PER_WEEK = 7;
const TOTAL_CALENDAR_CELLS = CALENDAR_WEEKS * DAYS_PER_WEEK;

export const formatToTwoDecimals = (v: string | number | null | undefined): string => {
	if (v == null || v === '' || isNaN(Number(v))) return '';
	return Number(v).toFixed(2);
};

export const safeFormatSecondsAsTime = (seconds: string | number | null | undefined): string => {
	if (seconds == null || seconds === '') return '';

	const n = Number(seconds);
	if (isNaN(n)) return '';

	return formatSecondsAsTime(n);
};

export const formatSecondsAsTime = (seconds: number): string => {
	const abs = Math.abs(seconds);

	const h = Math.floor(abs / 3600);
	const m = Math.floor((abs % 3600) / 60);

	const t = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

	return seconds < 0 ? `-${t}` : t;
};

export const weekdayShort = (dateStr: string): string => {
	return new Date(dateStr).toLocaleDateString(undefined, { weekday: 'short' });
};

export const formatDayDate = (dateStr: string): string => {
	return new Date(dateStr).toLocaleDateString(undefined, { month: '2-digit', day: '2-digit' });
};

export const formatDateRange = (start: Date, end: Date): string => {
	const formatOptions: Intl.DateTimeFormatOptions = {
		month: 'numeric',
		day: 'numeric',
		year: 'numeric',
	};

	return `${start.toLocaleDateString('en-US', formatOptions)} ~ ${end.toLocaleDateString('en-US', formatOptions)}`;
};

export const isSameDay = (date1: Date | null, date2: Date | null): boolean => {
	if (!date1 || !date2) {
		return false;
	}
	return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
};

const normalizeDate = (date: Date): number => {
	const normalized = new Date(date);
	normalized.setHours(0, 0, 0, 0);
	return normalized.getTime();
};

export const isDateInRange = (date: Date, start: Date, end: Date): boolean => {
	const dateTime = normalizeDate(date);
	const startTime = normalizeDate(start);
	const endTime = normalizeDate(end);

	return dateTime >= startTime && dateTime <= endTime;
};

export interface CalendarDay {
	date: Date;
	isCurrentMonth: boolean;
}

export const getDaysInMonth = (year: number, month: number): Array<CalendarDay> => {
	const firstDay = new Date(year, month, 1);
	const lastDay = new Date(year, month + 1, 0);
	const daysInMonth = lastDay.getDate();
	const startingDayOfWeek = firstDay.getDay();

	const days: Array<{ date: Date; isCurrentMonth: boolean }> = [];

	const prevMonthLastDay = new Date(year, month, 0).getDate();
	for (let i = startingDayOfWeek - 1; i >= 0; i--) {
		days.push({
			date: new Date(year, month - 1, prevMonthLastDay - i),
			isCurrentMonth: false,
		});
	}

	for (let i = 1; i <= daysInMonth; i++) {
		days.push({
			date: new Date(year, month, i),
			isCurrentMonth: true,
		});
	}

	const remainingDays = TOTAL_CALENDAR_CELLS - days.length;
	for (let i = 1; i <= remainingDays; i++) {
		days.push({
			date: new Date(year, month + 1, i),
			isCurrentMonth: false,
		});
	}

	return days;
};

/**
 * Converts total seconds into a HH:mm format.
 * Useful for duty start/end times stored as integers.
 */
export const secondsToTime = (seconds: number | null | undefined): string => {
	if (seconds === null || seconds === undefined) return '';
	const abs = Math.abs(seconds);
	const h = Math.floor(abs / 3600)
		.toString()
		.padStart(2, '0');
	const m = Math.round((abs % 3600) / 60)
		.toString()
		.padStart(2, '0');
	return (seconds < 0 ? '-' : '') + h + ':' + m;
};

/**
 * Formats an ISO string or Date into dd/MM/yy.
 * You can replace the local component formatDate with this.
 */
export const formatShortDate = (date: Date | string | undefined): string => {
	if (!date) return '-';
	const d = typeof date === 'string' ? new Date(date) : date;

	return new Intl.DateTimeFormat('en-GB', {
		day: '2-digit',
		month: '2-digit',
		year: '2-digit',
	}).format(d);
};

export const formatLocalizedDateTime = (value: string, locale: { dateTimeFormat: string }, timezone: string): string =>
	value ? DateTime.fromISO(value).setZone(timezone).toFormat(locale.dateTimeFormat) : '';

export const formatSecondsAsDuration = (seconds: number | null | undefined): string => {
	if (seconds === null || seconds === undefined) {
		return '';
	}
	const abs = Math.abs(seconds);
	const hours = Math.floor(abs / 3600);
	const minutes = Math.floor((abs % 3600) / 60);

	const parts: string[] = [];

	if (hours > 0) {
		parts.push(`${hours} hr${hours > 1 ? 's' : ''}`);
	}

	if (minutes > 0) {
		parts.push(`${minutes} min${minutes > 1 ? 's' : ''}`);
	}

	return parts.join(' ');
};
